/**
 * The ONE ffmpeg spawn a replay makes: fixed input → one MJPEG stream on
 * stdout, sampled at `fps` — never one spawn per frame. On a 10 s clip at
 * 4 fps that is 40 frames from ONE process instead of 40 seeks; on a hub
 * already carrying live decode + recording that is the difference between "a
 * run costs a second" and "a run costs a minute".
 *
 * `buildReplaySequenceArgs` deliberately does NOT match
 * `scripts/check-ffmpeg-primitive.ts`'s Rule 1 name pattern
 * (`build*(Ffmpeg|Transcode)*Args`) — this is not a second general-purpose
 * argv builder, it is the replay's own fixed sequence, the same naming
 * convention `buildRecordedStillArgs` (`recorded-still.ts:128`) already
 * uses for the same reason. The spawn below is a NEW literal
 * `spawn(ffmpeg, …)` site, so it is registered in
 * `SPAWN_ALLOWLIST` with its reason, per Rule 2.
 */
import { spawn, type ChildProcess } from 'node:child_process'

/**
 * ffmpeg argv for "one MJPEG stream, sampled at `fps`, from `startSec` for
 * `durationSec`, on stdout".
 *
 * `-ss` goes BEFORE `-i` — an INPUT seek, landing on the nearest keyframe,
 * cheap. After `-i` it would decode the whole file up to the instant and
 * throw the prefix away (`still-frame-service.ts:5-10` documents the same
 * choice for the recorder's export-dialog still).
 *
 * The sampling rate lives in the `fps=` FILTER, never in `-r`: `-r`
 * re-times the output by duplicating or dropping frames to hit a target
 * rate, which is not what a replay wants — it wants the frames that exist,
 * sampled at a fixed cadence, not a re-timed re-interpretation of them.
 *
 * `min(maxWidth,iw)` never upscales — a replay must never manufacture detail
 * a live detector would not have had.
 */
export function buildReplaySequenceArgs(opts: {
  readonly startSec: number
  readonly durationSec: number
  readonly fps: number
  readonly maxWidth: number
}): readonly string[] {
  const { startSec, durationSec, fps, maxWidth } = opts
  return [
    '-hide_banner',
    '-loglevel',
    'error',
    '-nostdin',
    '-ss',
    String(Math.max(0, startSec)),
    '-i',
    'pipe:0',
    '-t',
    String(Math.max(0, durationSec)),
    '-vf',
    `fps=${fps},scale='min(${maxWidth},iw)':-2`,
    '-f',
    'mjpeg',
    'pipe:1',
  ]
}

/**
 * Concurrent decodes per node. `RECORDED_STILL_MAX_CONCURRENT` (2) is the
 * value this repo has already chosen twice for a background maintenance
 * decode on a node that is also live-decoding and recording
 * (`recorded-still.ts:101`, `still-frame-service.ts:37`) — a replay run
 * carries the same "no deadline, shares the node" character, so it inherits
 * the number rather than picking a third one without a measurement (see the
 * plan's open unknowns).
 */
export const REPLAY_DECODE_MAX_CONCURRENT = 2

/** Hard ceiling for one replay decode. Copies `still-frame-service.ts:37` /
 *  `recorded-still-ports.ts:23` — the value this repo has already chosen
 *  twice for a bounded ffmpeg one-shot. */
export const REPLAY_DECODE_TIMEOUT_MS = 15_000

const KILL_GRACE_MS = 500

export interface ReplayDecoderDeps {
  readonly ffmpegPath: string
  readonly spawnFn?: (bin: string, args: readonly string[]) => ChildProcess
  readonly maxConcurrent?: number
  readonly timeoutMs?: number
}

/**
 * Runs the replay's fixed ffmpeg sequence, bounded.
 *
 * REFUSES beyond its bound rather than queuing — the same choice
 * `RecordedStillService` makes and for the same reason: an unbounded queue
 * would turn a bounded diagnostic run into backlog work whose value decays
 * while it waits. A caller that gets `busy` can retry; nothing here waits
 * for it.
 */
export class ReplayDecoder {
  private readonly ffmpegPath: string
  private readonly spawnFn: (bin: string, args: readonly string[]) => ChildProcess
  private readonly maxConcurrent: number
  private readonly timeoutMs: number
  private inFlight = 0

  constructor(deps: ReplayDecoderDeps) {
    this.ffmpegPath = deps.ffmpegPath
    this.spawnFn = deps.spawnFn ?? ((bin, args) => spawn(bin, [...args]))
    this.maxConcurrent = deps.maxConcurrent ?? REPLAY_DECODE_MAX_CONCURRENT
    this.timeoutMs = deps.timeoutMs ?? REPLAY_DECODE_TIMEOUT_MS
  }

  /** Decodes currently running (tests / metrics). */
  get running(): number {
    return this.inFlight
  }

  /**
   * Run the sequence once, feeding `input` on stdin (`-i pipe:0` in
   * {@link buildReplaySequenceArgs}) — the same shape as
   * `RecordedStillService`, which pipes a GOP rather than pointing ffmpeg at
   * a file path, because the source bytes (a recorder GOP window, or an
   * uploaded clip) are fetched through a capability call, not a local path.
   * Resolves with the whole MJPEG stdout stream.
   */
  async decodeSequence(args: readonly string[], input: Uint8Array): Promise<Buffer> {
    if (this.inFlight >= this.maxConcurrent) {
      throw new Error(
        `replay decode busy: node already running ${this.inFlight}/${this.maxConcurrent} decodes`,
      )
    }
    this.inFlight += 1
    try {
      return await this.runOnce(args, input)
    } finally {
      this.inFlight -= 1
    }
  }

  private runOnce(args: readonly string[], input: Uint8Array): Promise<Buffer> {
    return new Promise<Buffer>((resolve, reject) => {
      const chunks: Buffer[] = []
      const stderrTail: string[] = []
      let settled = false

      const child = (() => {
        try {
          return this.spawnFn(this.ffmpegPath, args)
        } catch (err) {
          reject(new Error(`replay decode: ffmpeg spawn failed: ${String(err)}`))
          return null
        }
      })()
      if (child === null) return

      const settle = (fn: () => void): void => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        fn()
      }

      const kill = (): void => {
        try {
          child.kill('SIGTERM')
        } catch {
          // already dead
        }
        const t = setTimeout(() => {
          if (child.exitCode === null && child.signalCode === null) {
            try {
              child.kill('SIGKILL')
            } catch {
              // already dead
            }
          }
        }, KILL_GRACE_MS)
        t.unref?.()
      }

      const timer = setTimeout(() => {
        settle(() => reject(new Error('replay decode: timed out')))
        kill()
      }, this.timeoutMs)
      timer.unref?.()

      child.stdout?.on('data', (chunk: Buffer) => chunks.push(chunk))
      child.stderr?.on('data', (data: Buffer) => {
        const line = data.toString().trim()
        if (line) {
          stderrTail.push(line)
          if (stderrTail.length > 8) stderrTail.shift()
        }
      })
      child.on('error', (err: Error) => {
        settle(() => reject(new Error(`replay decode: ffmpeg process error: ${err.message}`)))
      })
      child.on('exit', (code: number | null, signal: string | null) => {
        const stdout = Buffer.concat(chunks)
        if (code === 0) {
          settle(() => resolve(stdout))
          return
        }
        settle(() =>
          reject(
            new Error(
              `replay decode: ffmpeg exited code=${String(code)} signal=${String(signal)}: ${
                stderrTail.join(' | ') || '(no stderr)'
              }`,
            ),
          ),
        )
      })
      try {
        child.stdin?.write(Buffer.from(input.buffer, input.byteOffset, input.byteLength))
        child.stdin?.end()
      } catch {
        // stdin closed early (child already gone) — the exit handler settles.
      }
    })
  }
}
